import React from 'react'
import classnames from 'classnames'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import Image from 'next/image'
import { MaxWidthConstraint, PhotoHero, Spacer, WorkArea } from '@revelrygroup/components'

import { API } from '../revelry_api'
import { FuneralHome } from '../types'
import { FuneralHomeFooter } from '../components/FuneralHomeFooter'
import { FuneralHomeModel } from '../components/models/FuneralHomeModel'
import Layout from '../components/Layout'
import * as Showcase from '../components/Showcase'
import { Staff } from '../components/Staff'

interface Props {
  funeralHome: FuneralHome | null
}

const About: React.FC<Props> = ({ funeralHome }) => {
  return (
    <>
      <Head>
        <title>
          About Us - {funeralHome.name} - {FuneralHomeModel.headTagLocation(funeralHome)}
        </title>

        <meta
          name="description"
          content={`${funeralHome.name} has been serving communities with funeral services since the late 19th century. Our team of certified and experienced Funeral Directors is ready to serve your needs.`}
          key="description"
        />
      </Head>

      <Layout>
        <PhotoHero>About Us</PhotoHero>

        <div className="height-100">
          <main>
            <WorkArea className="blue-gradient-background">
              <MaxWidthConstraint maxWidth={975}>
                <h3 className={classnames('h3', 'centered-text')}>
                  Deeply rooted in the communities we serve, Kalas Funeral Home &amp; Crematory
                  believes each life deserves a celebration that shines a bright light on your loved
                  one&apos;s life, telling their unique story, and helping place family and friends
                  on a path toward peace and healing.
                </h3>
              </MaxWidthConstraint>
            </WorkArea>

            <WorkArea style={{ backgroundColor: 'white' }}>
              <MaxWidthConstraint>
                <h2 className={classnames('h2', 'centered-text')}>Our Staff</h2>

                <Spacer verticalTop="md">
                  <Staff />
                </Spacer>
              </MaxWidthConstraint>
            </WorkArea>

            <Showcase.Wrapper backgroundImage="/radian.png">
              <Showcase.Secondary>
                <Showcase.ImageContainer>
                  <Image
                    alt="Bob and George Kalas"
                    height={530}
                    layout="responsive"
                    src="/Bob-And-George-Kalas@2x.jpg"
                    width={433}
                  />
                </Showcase.ImageContainer>
              </Showcase.Secondary>

              <Showcase.Primary>
                <Showcase.Title>The Kalas Story</Showcase.Title>

                <Showcase.Text>
                  Since the late 19th century, five generations of the Mattingly/Kalas family have
                  served their communities with dignity and compassion. In 1889, William “Clement”
                  Mattingly opened the W.C. Mattingly Funeral Home in Leonardtown, MD, serving as
                  owner and funeral director for 42 years. Clement&apos;s son, Robert, followed in
                  his father&apos;s footsteps and, in 1945, branched out to open the Robert A.
                  Mattingly Funeral Home in southeast Washington, DC. He operated the DC location
                  for almost three decades with his wife, Sue, their daughter, Jean and her husband,
                  George P. Kalas, Sr. In 1973, the family relocated the funeral home to National
                  Harbor, MD where, after Robert Mattingly&apos;s death, George and Jean Kalas took
                  ownership and changed the name to the George P. Kalas Funeral Home.
                </Showcase.Text>

                <Showcase.Text>
                  The business grew rapidly under George and Jean&apos;s leadership and, as their
                  sons, George Jr. and Robert, joined them over the next decade, their reputation
                  for quality and compassion flourished. Once George Jr.&apos;s son, George III,
                  joined the business in the 1990s, the family expanded to a second location in 1997
                  outside of Annapolis in Edgewater, MD. A crematory was added in 2003 and remains
                  the only on-site crematory in the Annapolis area. In 2019, Robert&apos;s son,
                  Zachary, joined his father, earning his license as a crematory operator and
                  pursuing classes toward a degree in mortuary science. Crossing three centuries and
                  five generations, the Kalas Funeral Home and Crematory continues its proud
                  tradition of celebrating life&apos;s journey.
                </Showcase.Text>
              </Showcase.Primary>
            </Showcase.Wrapper>
          </main>

          <FuneralHomeFooter />
        </div>
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async () => {
  try {
    const funeralHome = await API.funeralHomes.load()

    return { props: { funeralHome } }
  } catch (e) {
    return { props: { funeralHome: null } }
  }
}

export default About
